<template>
  <div class="relative flex items-start">
    <!-- Client only to avoid hydration errors. No impact for seo since the hreflang is set in the head -->
    <ClientOnly>
      <SingleSelect
        v-if="alternates.length > 1"
        title="lang-switcher"
        :aria-label="$t('switch_language')"
        :model-value="{ slug: currentLocale, title: localeProperties.language }"
        placeholder="auswählen"
        :options="alternates?.map((l) => ({
          slug: l.locale,
          title: getLocaleLanguage(l.locale),
        })).filter((l) => l.title !== currentLocale)"
        uppercase
        :size="size"
        @update:model-value="switchLanguage"
      />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  size?: 'sm' | 'md'
}>()

const switchLocalePath = useSwitchLocalePath()
const { locale: currentLocale, locales, localeProperties } = useI18n()
const entry = inject(EntryInjectionKey, null)
const route = useRoute()

type Alternate = {
  locale: string
  url?: string
}

const alternates = computed<Alternate[]>(() => {
  if (Array.isArray(entry?.value?.alternates)) {
    // We have an entry from statamic
    return entry.value.alternates.filter(e => !!e) as Alternate[]
  } else {
    // We do not have an entry from statamic, likely using nuxt pages instead, so we offer all available locales
    return locales.value.filter(l => !l.isBusinessReport).map((l) => {
      return {
        locale: l.code,
      }
    })
  }
})

function getLocaleLanguage(locale: string) {
  const localeObj = locales.value.find(l => l.iso === locale)
  return localeObj ? localeObj.language : ''
}

function switchLanguage(event: any) {
  const selectedCode = event.title
  const selectedLocale = event.slug

  if (Array.isArray(alternates.value) && alternates.value.length) {
    const url = alternates.value.find(a => a.locale === selectedLocale)?.url

    if (url && url !== route.path) {
      switchLocalePath(selectedCode) // this is needed to work with different domains for different languages
      return navigateTo(url, {
        external: true,
      })
    }
  }

  navigateTo(switchLocalePath(selectedCode), {
    external: true, // this is needed to work with different domains for different languages
  })
}
</script>
